<template>
  <Dialog
    :is-open="isOpen"
    :title="$t('photo_report.title')"
    :close-label="$t('photo_report.close')"
    variant="modal"
    size="md"
    :prevent-overlay-close="isSubmitting || isCheckingExistingReport"
    @close="emit('close')"
  >
    <!-- Loading state while checking for existing report -->
    <div v-if="isCheckingExistingReport" class="loading-state">
      <LoadingIcon class="w-6 h-6 mr-2" />
      <span>{{ $t('photo_report.checking_existing') }}</span>
    </div>

    <!-- Existing report message -->
    <div v-else-if="hasExistingReport" class="existing-report-message">
      <div class="existing-report-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          class="existing-report-icon"
        >
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9s9 4.03 9 9z"
          />
        </svg>
        <h3 class="existing-report-title">
          {{ $t('photo_report.already_reported_title') }}
        </h3>
        <p class="existing-report-text">
          {{ $t('photo_report.already_reported_message') }}
        </p>
      </div>
    </div>

    <!-- Report form (only show if no existing report) -->
    <div v-else class="report-form">
      <!-- Report Category -->
      <div class="form-group">
        <label for="report-category" class="form-label">
          {{ $t('photo_report.category_label') }}
        </label>
        <select id="report-category" v-model="selectedCategory" class="form-select" required>
          <option value="">{{ $t('photo_report.select_category') }}</option>
          <option value="inappropriate_content">
            {{ $t('photo_report.categories.inappropriate_content') }}
          </option>
          <option value="spam">{{ $t('photo_report.categories.spam') }}</option>
          <option value="harassment">{{ $t('photo_report.categories.harassment') }}</option>
          <option value="violence">{{ $t('photo_report.categories.violence') }}</option>
          <option value="hate_speech">
            {{ $t('photo_report.categories.hate_speech') }}
          </option>
          <option value="nudity">{{ $t('photo_report.categories.nudity') }}</option>
          <option value="copyright">{{ $t('photo_report.categories.copyright') }}</option>
          <option value="other">{{ $t('photo_report.categories.other') }}</option>
        </select>
      </div>

      <!-- Optional Description -->
      <div class="form-group">
        <label for="report-description" class="form-label">
          {{ $t('photo_report.description_label') }}
          <span class="optional-text">({{ $t('photo_report.optional') }})</span>
        </label>
        <textarea
          id="report-description"
          v-model="description"
          class="form-textarea"
          :placeholder="$t('photo_report.description_placeholder')"
          rows="4"
          maxlength="500"
        ></textarea>
        <div class="character-count">{{ description.length }}/500</div>
      </div>

      <!-- Block User Option -->
      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="shouldBlockUser" class="form-checkbox" />
          <span class="checkbox-text">
            {{ $t('photo_report.block_user', { username: photo.username }) }}
          </span>
        </label>
        <p class="help-text">
          {{ $t('photo_report.block_user_help') }}
        </p>
      </div>
    </div>

    <template #footer>
      <button
        @click="emit('close')"
        class="cancel-button"
        :disabled="isSubmitting || isCheckingExistingReport"
      >
        {{ hasExistingReport ? $t('photo_report.close') : $t('photo_report.cancel') }}
      </button>
      <button
        v-if="!hasExistingReport && !isCheckingExistingReport"
        @click="handleSubmitReport"
        class="submit-button"
        :disabled="!canSubmit || isSubmitting"
      >
        <LoadingIcon v-if="isSubmitting" class="w-4 h-4 mr-2" />
        {{ $t('photo_report.submit') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { useSupabase } from '@/stores/supabase'
import LoadingIcon from '@/components/Icons/LoadingIcon.vue'
import Dialog from '@/components/Dialog.vue'
import type { Photo } from '@/stores/photos'

const { t } = useI18n()
const { showToast } = useToast()
const supabase = useSupabase()

const props = defineProps<{
  isOpen: boolean
  photo: Photo
}>()

const emit = defineEmits<{
  close: []
  reported: []
}>()

// Form state
const selectedCategory = ref('')
const description = ref('')
const shouldBlockUser = ref(false)
const isSubmitting = ref(false)
const hasExistingReport = ref(false)
const isCheckingExistingReport = ref(false)

const canSubmit = computed(() => {
  return (
    selectedCategory.value.trim().length > 0 &&
    supabase.user.status === 'SignedIn' &&
    !hasExistingReport.value
  )
})

const resetForm = () => {
  selectedCategory.value = ''
  description.value = ''
  shouldBlockUser.value = false
  isSubmitting.value = false
  hasExistingReport.value = false
  isCheckingExistingReport.value = false
}

const checkExistingReport = async () => {
  if (supabase.user.status !== 'SignedIn') return

  isCheckingExistingReport.value = true
  hasExistingReport.value = false

  try {
    const userId = supabase.user.status === 'SignedIn' ? supabase.user.data.id : null
    if (!userId) return

    const { data, error } = await supabase.api
      .from('photo_reports')
      .select('id')
      .eq('photo_id', props.photo.photo_id)
      .eq('reported_by', userId)
      .single()

    if (error && error.code !== 'PGRST116') {
      // PGRST116 is "no rows returned"
      console.error('Error checking existing report:', error)
      return
    }

    hasExistingReport.value = !!data
  } catch (error) {
    console.error('Error checking existing report:', error)
  } finally {
    isCheckingExistingReport.value = false
  }
}

const handleSubmitReport = async () => {
  if (!canSubmit.value || isSubmitting.value) return

  isSubmitting.value = true

  try {
    // Submit the report
    const { error: reportError } = await supabase.api.from('photo_reports').insert({
      photo_id: props.photo.photo_id,
      reported_by: supabase.user.status === 'SignedIn' ? supabase.user.data.id : null,
      reported_user_id: props.photo.user_id,
      category: selectedCategory.value,
      description: description.value.trim() || null
    })

    if (reportError) {
      console.error('Failed to submit report:', reportError)
      showToast(t('photo_report.submit_failed'))
      return
    }

    // Block user if requested
    if (shouldBlockUser.value) {
      const { error: blockError } = await supabase.api.from('blocked_users').insert({
        user_id_1: supabase.user.status === 'SignedIn' ? supabase.user.data.id : null,
        user_id_2: props.photo.user_id
      })

      if (blockError) {
        console.error('Failed to block user:', blockError)
        // Don't show error for blocking since the report was successful
      }
    }

    showToast(t('photo_report.submit_success'))
    emit('reported')
    emit('close')
    resetForm()
  } catch (error) {
    console.error('Error submitting report:', error)
    showToast(t('photo_report.submit_failed'))
  } finally {
    isSubmitting.value = false
  }
}

// Reset form and check for existing reports when modal opens
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      resetForm()
      await checkExistingReport()
    }
  }
)
</script>

<style scoped>
/* Component-specific styles */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: theme('spacing.8');
  color: theme('colors.stone.600');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.existing-report-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: theme('spacing.8');
}

.existing-report-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: theme('spacing.4');
  text-align: center;
  max-width: 300px;
}

.existing-report-icon {
  color: theme('colors.amber.500');

  .dark & {
    color: theme('colors.amber.400');
  }
}

.existing-report-title {
  font-size: theme('fontSize.lg');
  font-weight: theme('fontWeight.semibold');
  color: theme('colors.stone.900');
  margin: 0;

  .dark & {
    color: theme('colors.stone.100');
  }
}

.existing-report-text {
  font-size: theme('fontSize.sm');
  color: theme('colors.stone.600');
  margin: 0;
  line-height: theme('lineHeight.relaxed');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.form-group {
  margin-bottom: theme('spacing.6');
}

.form-label {
  display: block;
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.medium');
  color: theme('colors.stone.700');
  margin-bottom: theme('spacing.2');

  .dark & {
    color: theme('colors.stone.300');
  }
}

.optional-text {
  font-weight: theme('fontWeight.normal');
  color: theme('colors.stone.500');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.form-select,
.form-textarea {
  width: 100%;
  padding: theme('spacing.3');
  border: 1px solid theme('colors.stone.300');
  border-radius: theme('borderRadius.md');
  font-size: theme('fontSize.sm');
  color: theme('colors.stone.900');
  background: theme('colors.white');
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: theme('colors.blue.500');
    box-shadow: 0 0 0 3px theme('colors.blue.100');
  }

  .dark & {
    background: theme('colors.stone.800');
    border-color: theme('colors.stone.600');
    color: theme('colors.stone.100');

    &:focus {
      border-color: theme('colors.blue.400');
      box-shadow: 0 0 0 3px theme('colors.blue.900');
    }
  }
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.character-count {
  text-align: right;
  font-size: theme('fontSize.xs');
  color: theme('colors.stone.500');
  margin-top: theme('spacing.1');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: theme('spacing.3');
  cursor: pointer;
}

.form-checkbox {
  margin-top: theme('spacing.1');
  width: 16px;
  height: 16px;
  accent-color: theme('colors.blue.600');
}

.checkbox-text {
  font-size: theme('fontSize.sm');
  color: theme('colors.stone.700');
  line-height: theme('lineHeight.relaxed');

  .dark & {
    color: theme('colors.stone.300');
  }
}

.help-text {
  font-size: theme('fontSize.xs');
  color: theme('colors.stone.500');
  margin-top: theme('spacing.2');
  line-height: theme('lineHeight.relaxed');

  .dark & {
    color: theme('colors.stone.400');
  }
}

/* Footer button styles */
.cancel-button,
.submit-button {
  flex: 1;
  padding: theme('spacing.3') theme('spacing.4');
  border-radius: theme('borderRadius.md');
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.medium');
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.cancel-button {
  background: theme('colors.stone.100');
  color: theme('colors.stone.700');
  border: 1px solid theme('colors.stone.300');

  &:hover:not(:disabled) {
    background: theme('colors.stone.200');
  }

  .dark & {
    background: theme('colors.stone.800');
    color: theme('colors.stone.300');
    border-color: theme('colors.stone.600');

    &:hover:not(:disabled) {
      background: theme('colors.stone.700');
    }
  }
}

.submit-button {
  background: theme('colors.red.600');
  color: white;
  border: 1px solid theme('colors.red.600');

  &:hover:not(:disabled) {
    background: theme('colors.red.700');
    border-color: theme('colors.red.700');
  }

  .dark & {
    background: theme('colors.red.600');
    border-color: theme('colors.red.600');

    &:hover:not(:disabled) {
      background: theme('colors.red.700');
      border-color: theme('colors.red.700');
    }
  }
}
</style>
